import { useEffect, useState } from "react";
import { ScrollArea } from "../../ui/scroll-area";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { Calendar } from "../../ui/calendar";
import { Copy, ExternalLink, MousePointerClick, Trash2, Tag, CalendarClock, Pencil, X, Plus, Save, ChevronDown, ChevronUp, QrCode, Download, BarChart3, Power, Ban, CheckCircle, XCircle, Loader2, Link2, AlertTriangle } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../ui/collapsible";
import { useAuth } from '../../contexts/AuthContext';
import { UrlMapping, deleteUrl, updateUrl, updateShortCode, shortenUrl, getMyUrls, Workspace, activateUrl, deactivateUrl, validateShortCode, validateName, buildShortUrl, buildQrCodeUrl, getProtocol } from "../../utils/sqrone-api";
import { getDomainBrands } from "../../utils/branding-api";
import config from "../../utils/config";
import { format } from "date-fns";
import { toast } from "sonner";
import { AnalyticsPage } from "../analytics/AnalyticsPage";


interface ShortLinkDetailsProps {
    shortLink?: UrlMapping ;
    workspace?: Workspace | null;
    onCopy: (shortCode: string) => void;
    onDelete: (shortCode: string) => void;
    onUpdate: (updatedUrl: UrlMapping) => void;
    onCreate?: (newUrl: UrlMapping) => void;
    mode?: 'view' | 'create';
    onCancelCreate?: () => void;
    onBack?: () => void;
    isMobileView?: boolean;
  }

export function ShortLinkDetail({ shortLink, workspace, onCopy, onDelete, onUpdate, onCreate, mode = 'view', onCancelCreate, onBack, isMobileView = false }: ShortLinkDetailsProps) {
    const { token, isAuthenticated } = useAuth();

    const [isEditMode, setIsEditMode] = useState(false);
    const [editForm, setEditForm] = useState<UrlMapping | null>(null);
    const [newTag, setNewTag] = useState("");
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [startDatePickerOpen, setStartDatePickerOpen] = useState(false); // FR-33: Start date picker
    const [carriersPanelOpen, setCarriersPanelOpen] = useState(true);  // FS5: Carriers accordion
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [resolvedDomain, setResolvedDomain] = useState<string | null>(null);
    // Auto-detect SSL from config.apiBaseUrl by default
    const [resolvedSslEnabled, setResolvedSslEnabled] = useState<boolean>(config.apiBaseUrl.startsWith('https://'));
    const [createForm, setCreateForm] = useState<Partial<UrlMapping & { customShortCode?: string }>>({
      originalUrl: '',
      name: '',  // FS3: Checkpoint name
      description: '',  // FS4: Checkpoint description
      tags: '',
      expiresAt: '',
      startsAt: '', // FR-33: Start date
      customShortCode: '',
    });

    // FR-35: Custom short code validation state
    const [validationStatus, setValidationStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable'>('idle');
    const [validationMessage, setValidationMessage] = useState<string>('');

    // FS9: Name validation state (warning for duplicates, not error)
    const [nameValidationStatus, setNameValidationStatus] = useState<'idle' | 'checking' | 'unique' | 'duplicate'>('idle');
    const [nameValidationMessage, setNameValidationMessage] = useState<string>('');

    const displayData = mode === 'create' && createForm
      ? createForm
      : isEditMode && editForm
      ? editForm
      : shortLink;


    useEffect(() => {
        if (isAuthenticated) {
          //fetchUrls()
        }
    }, [shortLink, isAuthenticated]);

    // Pre-populate tags from workspace when creating a new link
    useEffect(() => {
        if (mode === 'create' && workspace) {
            setCreateForm(prev => ({
                ...prev,
                tags: workspace.tags || '',
            }));
        }
    }, [mode, workspace]);

    // FR-17 & FR-21: Resolve domain and SSL status when workspace has a domain brand
    useEffect(() => {
        const resolveDomainAndSsl = async () => {
            // Auto-detect SSL from config.apiBaseUrl
            const defaultSslEnabled = config.apiBaseUrl.startsWith('https://');
            const defaultDomain = config.apiBaseUrl.replace('http://', '').replace('https://', '');

            if (workspace?.domainBrandId && token) {
                try {
                    const domainBrands = await getDomainBrands(token);
                    const domainBrand = domainBrands.find(d => d.id === workspace.domainBrandId);
                    if (domainBrand && domainBrand.verified) {
                        setResolvedDomain(domainBrand.domain);
                        setResolvedSslEnabled(domainBrand.sslEnabled);
                    } else {
                        // Not verified, use default
                        setResolvedDomain(defaultDomain);
                        setResolvedSslEnabled(defaultSslEnabled);
                    }
                } catch (error) {
                    console.error('Failed to fetch domain brands:', error);
                    setResolvedDomain(defaultDomain);
                    setResolvedSslEnabled(defaultSslEnabled);
                }
            } else {
                // No domain brand, use default (auto-detected from config)
                setResolvedDomain(defaultDomain);
                setResolvedSslEnabled(defaultSslEnabled);
            }
        };

        if (mode === 'create' && workspace) {
            resolveDomainAndSsl();
        }
    }, [mode, workspace, token]);

    // FR-35: Debounced validation for custom short code
    useEffect(() => {
        const customCode = createForm.customShortCode?.trim();

        // Reset validation if empty or not in create mode with workspace
        if (!customCode || mode !== 'create' || !workspace || !token) {
            setValidationStatus('idle');
            setValidationMessage('');
            return;
        }

        // Don't validate if less than 3 characters (minimum length)
        if (customCode.length < 3) {
            setValidationStatus('idle');
            setValidationMessage('');
            return;
        }

        // Set checking status immediately
        setValidationStatus('checking');

        // Debounce the API call
        const timeoutId = setTimeout(async () => {
            try {
                const domain = resolvedDomain || config.apiBaseUrl.replace('http://', '').replace('https://', '');
                const result = await validateShortCode(customCode, domain, token);

                if (result.available) {
                    setValidationStatus('available');
                    setValidationMessage('✓ Short code is available');
                } else {
                    setValidationStatus('unavailable');
                    setValidationMessage(result.message);
                }
            } catch (error) {
                setValidationStatus('unavailable');
                setValidationMessage(error instanceof Error ? error.message : 'Failed to validate');
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [createForm.customShortCode, mode, workspace, token, resolvedDomain]);

    // FS9: Debounced validation for checkpoint name (warn if duplicate)
    useEffect(() => {
        // Get the name to validate based on mode
        const nameToValidate = mode === 'create'
            ? createForm.name?.trim()
            : isEditMode
            ? editForm?.name?.trim()
            : null;

        // Reset validation if empty or no token
        if (!nameToValidate || !token) {
            setNameValidationStatus('idle');
            setNameValidationMessage('');
            return;
        }

        // Don't validate if less than 2 characters
        if (nameToValidate.length < 2) {
            setNameValidationStatus('idle');
            setNameValidationMessage('');
            return;
        }

        // Set checking status immediately
        setNameValidationStatus('checking');

        // Debounce the API call
        const timeoutId = setTimeout(async () => {
            try {
                // Pass workspace ID if available, and exclude current URL ID when editing
                const excludeId = mode !== 'create' && shortLink ? shortLink.id : undefined;
                const result = await validateName(
                    nameToValidate,
                    token,
                    workspace?.id || null,
                    excludeId
                );

                if (result.exists) {
                    setNameValidationStatus('duplicate');
                    setNameValidationMessage(result.message);
                } else {
                    setNameValidationStatus('unique');
                    setNameValidationMessage('');
                }
            } catch (error) {
                // On error, don't block - just reset to idle
                setNameValidationStatus('idle');
                setNameValidationMessage('');
                console.error('Name validation error:', error);
            }
        }, 500); // 500ms debounce

        return () => clearTimeout(timeoutId);
    }, [createForm.name, editForm?.name, mode, isEditMode, workspace?.id, shortLink?.id, token]);

    const handleEdit = () => {
        if (shortLink) {
          setEditForm({ ...shortLink });
          setIsEditMode(true);
        }
    };

    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const handleDelete = async (shortCode: string) => {
        // Just call the parent's onDelete callback
        // The parent (ShortLinks.tsx) handles the actual deletion
        onDelete(shortCode);

        // Reset local state
        setIsEditMode(false);
        setEditForm(null);
    };

    // FR-22: Handle URL activation
    const handleActivate = async () => {
        if (!shortLink || !token) return;

        try {
            const updatedUrl = await activateUrl(shortLink.id, token);
            toast.success('URL activated successfully');
            onUpdate(updatedUrl);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to activate URL');
            console.error('Activate error:', err);
        }
    };

    // FR-22: Handle URL deactivation
    const handleDeactivate = async () => {
        if (!shortLink || !token) return;

        try {
            const updatedUrl = await deactivateUrl(shortLink.id, token);
            toast.success('URL deactivated successfully');
            onUpdate(updatedUrl);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to deactivate URL');
            console.error('Deactivate error:', err);
        }
    };

    const handleSave = async () => {
        if (!editForm || !shortLink) return;

        if (!token) {
            toast.error('You must be logged in to update URLs');
            return;
        }

        try {
            let currentUrl = shortLink;

            // FR-10a & FR-15: Check if shortCode has changed and update it first
            if (editForm.shortCode && editForm.shortCode !== shortLink.shortCode) {
                const updatedUrl = await updateShortCode(shortLink.id, editForm.shortCode, token);
                currentUrl = updatedUrl;
                toast.success('Short code updated successfully');
            }

            // Prepare update data with only the editable fields
            // Convert empty strings to undefined so they're excluded from JSON payload
            const updateData = {
                originalUrl: editForm.originalUrl,
                name: editForm.name || undefined,  // FS3: Checkpoint name
                description: editForm.description || undefined,  // FS4: Checkpoint description
                tags: editForm.tags || undefined,
                expiresAt: editForm.expiresAt || undefined,
                startsAt: editForm.startsAt || undefined, // FR-33
            };

            const finalUrl = await updateUrl(currentUrl.shortCode, updateData, token);
            toast.success('URL updated successfully');

            // Notify parent component of the update
            onUpdate(finalUrl);

            // Exit edit mode after successful save
            setIsEditMode(false);
            setEditForm(null);
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to update URL');
            console.error('Update error:', err);
        }
    };

    const handleCancel = () => {
        setIsEditMode(false);
        setEditForm(null);
        setNewTag("");
        if (mode === 'create') {
            // Reset create form to initial state with workspace tags
            setCreateForm({
                originalUrl: '',
                name: '',  // FS3: Reset checkpoint name
                description: '',  // FS4: Reset checkpoint description
                tags: workspace?.tags || '',
                expiresAt: '',
            });
            if (onCancelCreate) {
                onCancelCreate();
            }
            if (isMobileView && onBack) {
                onBack();
            }
        }
    };

    const handleCreateNew = async () => {
        if (!createForm.originalUrl?.trim()) {
            toast.error('Please enter a URL to shorten');
            return;
        }

        // FS8: Name is mandatory
        if (!createForm.name?.trim()) {
            toast.error('Please enter a name for this checkpoint');
            return;
        }

        if (!token) {
            toast.error('You must be logged in to create URLs');
            return;
        }

        // FR-35: Prevent submission if custom short code is unavailable
        if (createForm.customShortCode?.trim() && validationStatus === 'unavailable') {
            toast.error('Custom short code is already in use. Please choose a different one.');
            return;
        }

        try {
            // Resolve domain for this short URL
            let domain: string | undefined = undefined;

            // If URL is associated with a workspace that has a domain brand
            if (workspace?.domainBrandId) {
                try {
                    // Fetch domain brands from BrandService
                    const domainBrands = await getDomainBrands(token);
                    const domainBrand = domainBrands.find(d => d.id === workspace.domainBrandId);

                    // Use the domain if it's verified
                    if (domainBrand && domainBrand.verified) {
                        domain = domainBrand.domain;
                    }
                } catch (error) {
                    console.error('Failed to fetch domain brands:', error);
                    // Continue with default domain if BrandService fails
                }
            }

            // If no verified domain found, use default domain
            if (!domain) {
                const baseUrl = config.apiBaseUrl;
                domain = baseUrl.replace('http://', '').replace('https://', '');
            }

            // Call shorten URL API with workspace ID, domain, custom shortCode, and sslEnabled
            // The API now returns the full UrlMapping with all fields
            // FS8: Name is now mandatory - pass as second parameter
            const newUrl = await shortenUrl(
                createForm.originalUrl,
                createForm.name!.trim(),  // FS8: Mandatory name
                workspace?.id || null,
                token,
                domain,
                createForm.customShortCode?.trim() || undefined,
                resolvedSslEnabled
            );

            // If we have additional fields (name, description, tags, expiresAt, startsAt), update the URL
            if (createForm.name || createForm.description || createForm.tags || createForm.expiresAt || createForm.startsAt) {
                // Convert empty strings to undefined so they're excluded from JSON payload
                const updateData = {
                    originalUrl: createForm.originalUrl,
                    name: createForm.name || undefined,  // FS3: Checkpoint name
                    description: createForm.description || undefined,  // FS4: Checkpoint description
                    tags: createForm.tags || undefined,
                    expiresAt: createForm.expiresAt || undefined,
                    startsAt: createForm.startsAt || undefined, // FR-33
                };
                const updatedUrl = await updateUrl(newUrl.shortCode, updateData, token);
                toast.success('Short URL created successfully!');
                if (onCreate) {
                    onCreate(updatedUrl);
                }
            } else {
                // No additional fields, use the created URL directly
                toast.success('Short URL created successfully!');
                if (onCreate) {
                    onCreate(newUrl);
                }
            }

            // Reset create form, but keep workspace tags if in a workspace
            setCreateForm({
                originalUrl: '',
                name: '',  // FS3: Reset checkpoint name
                description: '',  // FS4: Reset checkpoint description
                tags: workspace?.tags || '',
                expiresAt: '',
            });
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Failed to create short URL');
            console.error('Create error:', err);
        }
    };

    const handleAddTag = () => {
        if (!newTag.trim()) return;

        const trimmedNewTag = newTag.trim();

        if (mode === 'create') {
            const currentTags = createForm.tags ? createForm.tags.split(',').filter(t => t.trim()) : [];
            if (currentTags.includes(trimmedNewTag)) {
                setNewTag("");
                return;
            }
            const updatedTags = [...currentTags, trimmedNewTag].join(',');
            setCreateForm({ ...createForm, tags: updatedTags });
            setNewTag("");
        } else if (editForm) {
            const currentTags = editForm.tags ? editForm.tags.split(',').filter(t => t.trim()) : [];
            if (currentTags.includes(trimmedNewTag)) {
                setNewTag("");
                return;
            }
            const updatedTags = [...currentTags, trimmedNewTag].join(',');
            setEditForm({ ...editForm, tags: updatedTags });
            setNewTag("");
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        if (mode === 'create') {
            const currentTags = createForm.tags ? createForm.tags.split(',').filter(t => t.trim()) : [];
            const updatedTags = currentTags.filter((t) => t !== tagToRemove).join(',');
            setCreateForm({ ...createForm, tags: updatedTags });
        } else if (editForm) {
            const currentTags = editForm.tags ? editForm.tags.split(',').filter(t => t.trim()) : [];
            const updatedTags = currentTags.filter((t) => t !== tagToRemove).join(',');
            setEditForm({ ...editForm, tags: updatedTags });
        }
    };

      const handleDateSelect = (date: Date | undefined) => {
        if (date) {
          // Format date in local timezone to avoid timezone shift issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;

          if (mode === 'create') {
            setCreateForm({ ...createForm, expiresAt: dateString });
          } else if (editForm) {
            setEditForm({ ...editForm, expiresAt: dateString });
          }
        }
        setDatePickerOpen(false);
      };

      const handleClearExpiration = () => {
        if (mode === 'create') {
          setCreateForm({ ...createForm, expiresAt: '' });
        } else if (editForm) {
          setEditForm({ ...editForm, expiresAt: "" });
        }
        setDatePickerOpen(false);
      };

      // FR-33: Start date handlers
      const handleStartDateSelect = (date: Date | undefined) => {
        if (date) {
          // Format date in local timezone to avoid timezone shift issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const dateString = `${year}-${month}-${day}`;

          if (mode === 'create') {
            setCreateForm({ ...createForm, startsAt: dateString });
          } else if (editForm) {
            setEditForm({ ...editForm, startsAt: dateString });
          }
        }
        setStartDatePickerOpen(false);
      };

      const handleClearStartDate = () => {
        if (mode === 'create') {
          setCreateForm({ ...createForm, startsAt: '' });
        } else if (editForm) {
          setEditForm({ ...editForm, startsAt: "" });
        }
        setStartDatePickerOpen(false);
      };

      const handleDownloadQR = async () => {
        if (!displayData?.shortCode) return;
        const qrUrl = getQrCodeUrl();
        if (!qrUrl) return;

        try {
          // Fetch the QR code image from the backend (includes campaign logo if available)
          const response = await fetch(qrUrl);
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `qr-${displayData.shortCode}.png`;
          link.click();
          URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Failed to download QR code:", error);
          toast.error("Failed to download QR code");
        }
      };

      const getDomain = (): string => {
        // Use domain field if available, otherwise extract from shortUrl
        if (displayData?.domain) {
          return displayData.domain;
        }
        if (displayData?.shortUrl) {
          try {
            const url = new URL(displayData.shortUrl);
            return url.host; // Returns "example.com:8080" or "example.com"
          } catch {
            return config.apiBaseUrl.replace('http://', '').replace('https://', '');
          }
        }
        return config.apiBaseUrl.replace('http://', '').replace('https://', '');
      };

      // FR-20 & FR-21: Construct full short URL with correct domain and SSL status
      const getFullShortUrl = (): string => {
        if (!displayData?.shortCode) return '';
        const domain = getDomain();
        const sslEnabled = displayData.sslEnabled ?? true;
        return buildShortUrl(displayData.shortCode, domain, sslEnabled);
      };

      // Construct QR code URL with correct domain and SSL status (handles localhost correctly)
      const getQrCodeUrl = (): string => {
        if (!displayData?.shortCode) return '';
        const domain = getDomain();
        const sslEnabled = displayData.sslEnabled ?? true;
        return buildQrCodeUrl(displayData.shortCode, domain, sslEnabled);
      };

    if (!isAuthenticated) {
        return (
          <div className="shortlinks">
            <div className="auth-required">
              <p>Please sign in to manage your links.</p>
            </div>
          </div>
        );
    }

    if (showAnalytics && shortLink) {
      return <AnalyticsPage onBack={() => setShowAnalytics(false)} urlMappingId={shortLink.id} />;
    }

    return (
        <div className="h-full bg-background">
        {!displayData ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <ExternalLink className="size-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Select a Checkpoint to view details</h3>
              <p className="text-sm text-muted-foreground">
                Choose a Checkpoint from the list to see its details
              </p>
            </div>
          </div>
        ) : (
          <ScrollArea className="h-full">
            <div className="p-6 space-y-4">
              {/* Back button for mobile view */}
              {isMobileView && onBack && (
                <Button variant="ghost" onClick={onBack} className="mb-2 md:hidden">
                  ← Back to URLs
                </Button>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-bold mb-2">
                      {mode === 'create' ? 'Create New Checkpoint' : 'Details'}
                    </h1>
                    {mode !== 'create' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setShowAnalytics(true)}
                        title="View Analytics"
                      >
                        <BarChart3 className="size-4" />
                      </Button>
                    )}
                  </div>
                  <p className="text-muted-foreground">
                    {mode === 'create'
                      ? "Enter the URL or action this checkpoint leads to"
                      : isEditMode
                      ? "Edit the URL or action this checkpoint leads to"
                      : "View and manage your URL or action this checkpoint leads to"}
                  </p>
                </div>
                {isEditMode && mode !== 'create' && (
                  <Badge variant="outline" className="gap-1">
                    <Pencil className="size-3" />
                    Editing
                  </Badge>
                )}
              </div>

              {/* Custom Short Code - Show only in create mode for workspace URLs */}
              {mode === 'create' && workspace && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Custom Short Code (optional)
                  </label>
                  <div className={`flex items-center gap-0 border rounded-md overflow-hidden ${
                    validationStatus === 'available' ? 'border-green-500' :
                    validationStatus === 'unavailable' ? 'border-red-500' :
                    ''
                  }`}>
                    <span className="px-3 py-2 bg-muted text-sm font-mono text-muted-foreground border-r">
                      {/* FR-17: Show the resolved domain */}
                      {resolvedDomain || `${config.apiBaseUrl.replace('http://', '').replace('https://', '')}`}/
                    </span>
                    <Input
                      value={createForm.customShortCode || ''}
                      onChange={(e) => setCreateForm({ ...createForm, customShortCode: e.target.value })}
                      className="flex-1 font-mono text-sm border-0 focus-visible:ring-0"
                      placeholder="my-custom-link"
                    />
                    {validationStatus === 'checking' && (
                      <Loader2 className="size-4 mx-3 animate-spin text-muted-foreground" />
                    )}
                    {validationStatus === 'available' && (
                      <CheckCircle className="size-4 mx-3 text-green-500" />
                    )}
                    {validationStatus === 'unavailable' && (
                      <XCircle className="size-4 mx-3 text-red-500" />
                    )}
                  </div>
                  {validationMessage && (
                    <p className={`text-xs ${
                      validationStatus === 'available' ? 'text-green-600' :
                      validationStatus === 'unavailable' ? 'text-red-600' :
                      'text-muted-foreground'
                    }`}>
                      {validationMessage}
                    </p>
                  )}
                  {!validationMessage && (
                    <p className="text-xs text-muted-foreground">
                      Leave empty to auto-generate. Only letters, numbers, hyphens, and underscores allowed (3-50 characters).
                    </p>
                  )}
                </div>
              )}

              {/* FS5 Order: 1. Name */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Name {mode === 'create' && <span className="text-red-500">*</span>}
                </label>
                {(isEditMode || mode === 'create') ? (
                  <>
                    <div className={`flex items-center gap-0 border rounded-md overflow-hidden ${
                      nameValidationStatus === 'duplicate' ? 'border-amber-500' :
                      nameValidationStatus === 'unique' ? 'border-green-500' :
                      ''
                    }`}>
                      <Input
                        value={displayData.name || ''}
                        onChange={(e) => {
                          if (mode === 'create') {
                            setCreateForm({ ...createForm, name: e.target.value });
                          } else {
                            setEditForm(editForm ? { ...editForm, name: e.target.value } : null);
                          }
                        }}
                        className="text-sm border-0 focus-visible:ring-0"
                        placeholder="Give your checkpoint a friendly name"
                        maxLength={255}
                      />
                      {nameValidationStatus === 'checking' && (
                        <Loader2 className="size-4 mx-3 animate-spin text-muted-foreground" />
                      )}
                      {nameValidationStatus === 'unique' && (
                        <CheckCircle className="size-4 mx-3 text-green-500" />
                      )}
                      {nameValidationStatus === 'duplicate' && (
                        <AlertTriangle className="size-4 mx-3 text-amber-500" />
                      )}
                    </div>
                    {/* FS9: Warning message for duplicate names */}
                    {nameValidationMessage && nameValidationStatus === 'duplicate' && (
                      <p className="text-xs text-amber-600">
                        ⚠ {nameValidationMessage}. You can still proceed if you want.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[36px] flex items-center">
                    {displayData.name || (
                      <span className="text-muted-foreground">No name set</span>
                    )}
                  </div>
                )}
              </div>

              {/* FS5 Order: 2. Checkpoint for (Original URL) */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  {mode === 'create' ? 'Destination URL' : 'Checkpoint for'} {mode === 'create' && <span className="text-red-500">*</span>}
                </label>
                <div className="flex items-start gap-2">
                  {isEditMode || mode === 'create' ? (
                    <Input
                      value={displayData.originalUrl}
                      onChange={(e) => {
                        if (mode === 'create') {
                          setCreateForm({ ...createForm, originalUrl: e.target.value });
                        } else {
                          setEditForm(editForm ? { ...editForm, originalUrl: e.target.value } : null);
                        }
                      }}
                      className="flex-1 font-mono text-sm"
                      placeholder="https://example.com/your-long-url"
                    />
                  ) : (
                    <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono break-all">
                      {displayData.originalUrl}
                    </code>
                  )}
                  {mode !== 'create' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleCopy(displayData.originalUrl!)}
                    >
                      <Copy />
                      Copy
                    </Button>
                  )}
                </div>
              </div>

              {/* FS5 Order: 3. Activation Status - Hide in create mode */}
              {mode !== 'create' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">Activation Status</label>
                  <div className="flex items-center justify-between p-4 bg-muted rounded-md">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${displayData.isActive ? 'bg-green-100 dark:bg-green-950/30' : 'bg-red-100 dark:bg-red-950/30'}`}>
                        {displayData.isActive ? (
                          <Power className="size-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <Ban className="size-5 text-red-600 dark:text-red-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant={displayData.isActive ? "default" : "destructive"}>
                            {displayData.isActive ? "Active" : "Inactive"}
                          </Badge>
                          {!displayData.isActive && displayData.deactivationSource && (
                            <Badge variant="outline" className="text-xs">
                              {displayData.deactivationSource === 'Collection' ? 'Deactivated by Collection' :
                               displayData.deactivationSource === 'Geo' ? 'Geo-blocked' :
                               'Manually Deactivated'}
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {displayData.isActive
                            ? 'This link is currently active and accessible'
                            : 'This link is deactivated and will return 404 or redirect'}
                        </div>
                        {!displayData.isActive && displayData.deactivatedAt && (
                          <div className="text-xs text-muted-foreground mt-1">
                            Deactivated on {new Date(displayData.deactivatedAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                    {!isEditMode && displayData.deactivationSource !== 'Collection' && (
                      <Button
                        size="sm"
                        variant={displayData.isActive ? "outline" : "default"}
                        onClick={displayData.isActive ? handleDeactivate : handleActivate}
                      >
                        {displayData.isActive ? (
                          <>
                            <Ban className="size-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="size-4" />
                            Activate
                          </>
                        )}
                      </Button>
                    )}
                    {displayData.deactivationSource === 'Workspace' && !displayData.isActive && (
                      <p className="text-xs text-muted-foreground italic">
                        Activate the workspace to enable this URL
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* FS5 Order: 4. Description */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground">
                  Description (optional)
                </label>
                {(isEditMode || mode === 'create') ? (
                  <Textarea
                    value={displayData.description || ''}
                    onChange={(e) => {
                      if (mode === 'create') {
                        setCreateForm({ ...createForm, description: e.target.value });
                      } else {
                        setEditForm(editForm ? { ...editForm, description: e.target.value } : null);
                      }
                    }}
                    className="text-sm min-h-[80px] resize-none"
                    placeholder="Add a description for this checkpoint"
                    maxLength={1000}
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md text-sm min-h-[36px]">
                    {displayData.description || (
                      <span className="text-muted-foreground">No description set</span>
                    )}
                  </div>
                )}
              </div>

              {/* FS5 Order: 5. Tags */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">Tags (optional)</label>
                  {mode === 'create' && workspace?.tags && (
                    <span className="text-xs text-muted-foreground">
                      From workspace: {workspace.name}
                    </span>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2 px-3 py-3 bg-muted rounded-md min-h-[48px]">
                    {displayData.tags && displayData.tags?.split(',').filter(t => t.trim()).length > 0 ? (
                      displayData.tags!.split(',').filter(t => t.trim()).map((tag) => (
                        <Badge key={tag} variant="secondary" className="gap-1">
                          <Tag className="size-3" />
                          {tag}
                          {(isEditMode || mode === 'create') && (
                            <button
                              onClick={() => handleRemoveTag(tag)}
                              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5 transition-colors"
                            >
                              <X className="size-3" />
                            </button>
                          )}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">No tags</span>
                    )}
                  </div>
                  {(isEditMode || mode === 'create') && (
                    <div className="flex gap-2">
                      <Input
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && handleAddTag()}
                        placeholder="Add a tag"
                        className="flex-1"
                      />
                      <Button size="sm" onClick={handleAddTag} disabled={!newTag.trim()}>
                        <Plus />
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* FS5 Order: 6. Carriers Accordion - Short URL and QR Code */}
              {mode !== 'create' && (
                <Collapsible open={carriersPanelOpen} onOpenChange={setCarriersPanelOpen}>
                  <CollapsibleTrigger asChild>
                    <div
                      role="button"
                      tabIndex={0}
                      className="flex items-center justify-between w-full px-4 py-3 bg-muted/50 hover:bg-muted rounded-md cursor-pointer transition-colors"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setCarriersPanelOpen(!carriersPanelOpen);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <Link2 className="size-4 text-muted-foreground" />
                        <span className="text-sm font-medium">Carriers</span>
                        <Badge variant="secondary" className="text-xs">
                          Short URL & QR Code
                        </Badge>
                      </div>
                      {carriersPanelOpen ? (
                        <ChevronUp className="size-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="size-4 text-muted-foreground" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-4 mt-3">
                    {/* Short URL */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">Short URL</label>
                      <div className="flex items-center gap-2">
                        {isEditMode ? (
                          <div className="flex-1 flex items-center gap-0 border rounded-md overflow-hidden">
                            <span className="px-3 py-2 bg-muted text-sm font-mono text-muted-foreground border-r">
                              {getProtocol(getDomain(), displayData.sslEnabled ?? true)}{getDomain()}/
                            </span>
                            <Input
                              value={displayData.shortCode}
                              onChange={(e) =>
                                setEditForm(editForm ? { ...editForm, shortCode: e.target.value } : null)
                              }
                              className="flex-1 font-mono text-sm"
                              placeholder="Enter short URL"
                            />
                          </div>
                        ) : (
                          <code className="flex-1 px-3 py-2 bg-muted rounded-md text-sm font-mono">
                            {getFullShortUrl()}
                          </code>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopy(getFullShortUrl())}
                        >
                          <Copy />
                          Copy
                        </Button>
                      </div>
                    </div>

                    {/* QR Code */}
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-muted-foreground">QR Code</label>
                      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-muted rounded-md">
                        <div className="flex-shrink-0">
                          <div className="p-3 bg-white rounded-lg shadow-sm border">
                            {displayData.shortCode && (
                              <img
                                src={getQrCodeUrl()}
                                alt="QR Code"
                                width={128}
                                height={128}
                                className="block"
                              />
                            )}
                          </div>
                        </div>
                        <div className="flex-1 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <QrCode className="size-4 text-muted-foreground" />
                              <span className="text-sm font-medium">Scannable QR Code</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                              Share this QR code to allow users to quickly access your checkpoint by scanning with their mobile device.
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownloadQR}
                            className="w-full sm:w-auto"
                          >
                            <Download className="size-4" />
                            Download QR Code
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}

              {/* FS5 Order: 7. Number of access - Hide in create mode */}
              {mode !== 'create' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Number of access
                    {isEditMode && (
                      <span className="ml-2 text-xs text-muted-foreground">(read-only)</span>
                    )}
                  </label>
                  <div className="flex items-center gap-3 px-3 py-3 bg-muted rounded-md">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <MousePointerClick className="size-5 text-primary" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">
                        {displayData.clicks?.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground">Total access count</div>
                    </div>
                  </div>
                </div>
              )}

              {/* FS5 Order: 8. Start Date */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">
                    Start Date (optional)
                  </label>
                  {displayData.startsAt && !displayData.hasCustomStartsAt && workspace && (
                    <Badge variant="secondary" className="text-xs">
                      Inherited from workspace
                    </Badge>
                  )}
                </div>
                {(isEditMode || mode === 'create') ? (
                  <Popover open={startDatePickerOpen} onOpenChange={setStartDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <div
                        role="button"
                        tabIndex={0}
                        className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 px-4 py-2 w-full text-left font-normal cursor-pointer"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            setStartDatePickerOpen(true);
                          }
                        }}
                      >
                        <CalendarClock className="size-4" />
                        {displayData.startsAt
                          ? format(new Date(displayData.startsAt), "PPP")
                          : "No start date"}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          displayData.startsAt
                            ? new Date(displayData.startsAt)
                            : undefined
                        }
                        onSelect={handleStartDateSelect}
                        initialFocus
                      />
                      {displayData.startsAt && (
                        <div className="p-3 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full"
                            onClick={handleClearStartDate}
                          >
                            Clear start date
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex items-center gap-3 px-3 py-3 bg-muted rounded-md">
                    <div className="p-2 bg-secondary rounded-lg">
                      <CalendarClock className="size-5" />
                    </div>
                    <div>
                      {displayData.startsAt ? (
                        <>
                          <div className="text-lg font-semibold">
                            {new Date(displayData.startsAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(() => {
                              const daysUntil = Math.floor(
                                (new Date(displayData.startsAt).getTime() - Date.now()) /
                                  (1000 * 60 * 60 * 24)
                              );
                              if (daysUntil < 0) {
                                return `Started ${Math.abs(daysUntil)} days ago`;
                              } else if (daysUntil === 0) {
                                return "Starts today";
                              } else {
                                return `Starts in ${daysUntil} days`;
                              }
                            })()}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-semibold">No start date</div>
                          <div className="text-xs text-muted-foreground">Available immediately</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* FS5 Order: 9. Expiration Date */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-muted-foreground">
                    Expiration Date (TTL) (optional)
                  </label>
                  {displayData.expiresAt && !displayData.hasCustomExpiresAt && workspace && (
                    <Badge variant="secondary" className="text-xs">
                      Inherited from workspace
                    </Badge>
                  )}
                </div>
                {(isEditMode || mode === 'create') ? (
                  <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                    {/* <PopoverTrigger className="w-full"> */}
                    <PopoverTrigger asChild> 
                        <div
                            role="button"
                            tabIndex={0}
                            className="inline-flex items-center justify-start gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 h-9 px-4 py-2 w-full text-left font-normal cursor-pointer"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setDatePickerOpen(true);
                              }
                            }}
                          >
                        <CalendarClock className="size-4" />
                        {displayData.expiresAt
                          ? format(new Date(displayData.expiresAt), "PPP")
                          : "No expiration"}
                      </div>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={
                          displayData.expiresAt
                            ? new Date(displayData.expiresAt)
                            : undefined
                        }
                        onSelect={handleDateSelect}
                        initialFocus
                      />
                      {displayData.expiresAt && (
                        <div className="p-3 border-t">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full"
                            onClick={handleClearExpiration}
                          >
                            Clear expiration
                          </Button>
                        </div>
                      )}
                    </PopoverContent>
                  </Popover>
                ) : (
                  <div className="flex items-center gap-3 px-3 py-3 bg-muted rounded-md">
                    <div className="p-2 bg-secondary rounded-lg">
                      <CalendarClock className="size-5" />
                    </div>
                    <div>
                      {displayData.expiresAt ? (
                        <>
                          <div className="text-lg font-semibold">
                            {new Date(displayData.expiresAt).toLocaleDateString("en-US", {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            })}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {(() => {
                              const daysUntil = Math.floor(
                                (new Date(displayData.expiresAt).getTime() - Date.now()) /
                                  (1000 * 60 * 60 * 24)
                              );
                              if (daysUntil < 0) {
                                return `Expired ${Math.abs(daysUntil)} days ago`;
                              } else if (daysUntil === 0) {
                                return "Expires today";
                              } else {
                                return `Expires in ${daysUntil} days (TTL: ${daysUntil}d)`;
                              }
                            })()}
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="text-lg font-semibold">No expiration</div>
                          <div className="text-xs text-muted-foreground">Link never expires</div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-4">
                {mode === 'create' ? (
                  <>
                    <Button className="flex-1" onClick={handleCreateNew}>
                      <Plus />
                      Create Short URL
                    </Button>
                    <Button variant="outline" onClick={handleCancel}>
                      <X />
                      Cancel
                    </Button>
                  </>
                ) : isEditMode ? (
                  <>
                    <Button className="flex-1" onClick={handleSave}>
                      <Save />
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={handleCancel}>
                      <X />
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={handleEdit}>
                      <Pencil />
                      Edit
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => handleCopy(getFullShortUrl())}
                    >
                      <Copy />
                      Copy Short URL
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleDelete(displayData.shortCode!)}
                    >
                      <Trash2 />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </div>
          </ScrollArea>
        )}
      </div>
    )
};
